import React from "react";
import { useTranslation } from "react-i18next";

import { PlacesAutocomplete } from "@/components/places-autocomplete";
import { notify } from "@/utils";
import { Logger } from "@/utils/logger";

import { useWorkerOnboarding } from "../-worker-onboarding-context";

interface Props {
  onContinue?: (handler: () => Promise<boolean>) => void;
}

export function WorkerLocationStep({ onContinue }: Props) {
  const { t } = useTranslation("onboarding");
  const { data, patch, saveDraft } = useWorkerOnboarding();

  React.useEffect(() => {
    if (!onContinue) return;
    onContinue(async () => {
      if (!data.city.trim()) {
        notify.error(t("workerLocation.cityRequired"));
        return false;
      }
      // Employers' worker-search filters by Haversine(business → worker)
      // against the worker's commute radius. Without lat/lng on the profile,
      // the worker is invisible to every employer search, so refuse to save
      // a city string the user just typed by hand.
      if (data.latitude == null || data.longitude == null) {
        notify.error(t("workerLocation.pickFromSuggestions"));
        return false;
      }
      try {
        await saveDraft();
        return true;
      } catch (e) {
        Logger.error("worker onboarding save failed at location", {
          error: e instanceof Error ? e.message : String(e),
        });
        notify.error(
          e instanceof Error ? e.message : t("workerLocation.saveFailed"),
        );
        return false;
      }
    });
  }, [onContinue, data, saveDraft, t]);

  return (
    <section className="w-full flex flex-col gap-5">
      <div>
        <h2 className="heading-1">{t("workerLocation.title")}</h2>
        <p className="body-small text-gray-600 mt-2">
          {t("workerLocation.description")}
        </p>
      </div>
      <div className="flex flex-col gap-4 max-w-[420px]">
        <PlacesAutocomplete
          label={t("workerLocation.cityOrZipLabel")}
          placeholder={t("workerLocation.cityOrZipPlaceholder")}
          value={data.city}
          onChange={(e) =>
            patch({
              city: e.target.value,
              latitude: undefined,
              longitude: undefined,
            })
          }
          onPlaceSelect={(place) => {
            if (!place) return;
            const formatted = place.formattedAddress ?? place.name ?? "";
            patch({
              city: formatted,
              latitude: place.location?.lat,
              longitude: place.location?.lng,
            });
          }}
        />
        <div className="flex flex-col gap-2">
          <label className="body-small text-gray-700">
            {t("workerLocation.distanceLabel", { radius: data.radius })}
          </label>
          <input
            type="range"
            min={2}
            max={50}
            step={1}
            value={data.radius}
            onChange={(e) =>
              patch({ radius: Number(e.currentTarget.value) })
            }
            className="w-full accent-garnet"
          />
        </div>
      </div>
    </section>
  );
}
